import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ContactService } from '@/services/contactService-new';
import { toast } from 'sonner';
import { Loader2, Plus, X } from 'lucide-react';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('QuickAddBorrowerModal', 'contacts');
const contactService = new ContactService();

const HONORIFIC_OPTIONS = [
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'Ms.', label: 'Ms.' },
  { value: 'Mx.', label: 'Mx.' },
  { value: 'Rev.', label: 'Rev.' },
  { value: 'Prof.', label: 'Prof.' }
];

interface QuickAddBorrowerModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface BorrowerFormData {
  salutation?: string;
  firstName: string;
  lastName: string;
  email: string;
  mobilePhone: string;
}

export function QuickAddBorrowerModal({ open, onClose, onSuccess }: QuickAddBorrowerModalProps) {
  const [loading, setLoading] = useState(false);
  const [addCoBorrower, setAddCoBorrower] = useState(false);

  const [borrower, setBorrower] = useState<BorrowerFormData>({
    salutation: '',
    firstName: '',
    lastName: '',
    email: '',
    mobilePhone: ''
  });

  const [coBorrower, setCoBorrower] = useState<BorrowerFormData>({
    salutation: '',
    firstName: '',
    lastName: '',
    email: '',
    mobilePhone: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate primary borrower
    if (!borrower.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!borrower.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // At least one contact method required (email OR phone)
    const hasEmail = borrower.email.trim().length > 0;
    const hasPhone = borrower.mobilePhone.trim().length > 0;

    if (!hasEmail && !hasPhone) {
      newErrors.email = 'Email or cell phone is required';
      newErrors.mobilePhone = 'Email or cell phone is required';
    } else {
      // If email is provided, validate format
      if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(borrower.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    }

    // Validate co-borrower if checkbox is checked
    if (addCoBorrower) {
      if (!coBorrower.firstName.trim()) {
        newErrors.coBorrowerFirstName = 'Co-borrower first name is required';
      }
      if (!coBorrower.lastName.trim()) {
        newErrors.coBorrowerLastName = 'Co-borrower last name is required';
      }

      // At least one contact method required for co-borrower
      const coBorrowerHasEmail = coBorrower.email.trim().length > 0;
      const coBorrowerHasPhone = coBorrower.mobilePhone.trim().length > 0;

      if (!coBorrowerHasEmail && !coBorrowerHasPhone) {
        newErrors.coBorrowerEmail = 'Email or cell phone is required';
        newErrors.coBorrowerMobilePhone = 'Email or cell phone is required';
      } else {
        // If email is provided, validate format
        if (coBorrowerHasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(coBorrower.email)) {
          newErrors.coBorrowerEmail = 'Please enter a valid email';
        }

        // Check that co-borrower email is different from primary borrower email
        if (coBorrowerHasEmail && hasEmail &&
            borrower.email.trim().toLowerCase() === coBorrower.email.trim().toLowerCase()) {
          newErrors.coBorrowerEmail = 'Co-borrower must have a different email address than the primary borrower';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      // Check if primary borrower email already exists
      if (borrower.email.trim()) {
        const borrowerEmailExists = await contactService.emailExists(borrower.email.trim());
        if (borrowerEmailExists) {
          toast.error(
            `A contact with email "${borrower.email}" already exists in your organization. ` +
            `Please use a different email or update the existing contact.`
          );
          setLoading(false);
          return;
        }
      }

      // Check if primary borrower phone already exists
      if (borrower.mobilePhone.trim()) {
        const borrowerPhoneExists = await contactService.phoneExists(borrower.mobilePhone.trim());
        if (borrowerPhoneExists) {
          toast.error(
            `A contact with this phone number already exists in your organization. ` +
            `Please use a different phone or update the existing contact.`
          );
          setLoading(false);
          return;
        }
      }

      // Check if co-borrower email already exists
      if (addCoBorrower && coBorrower.email.trim()) {
        const coBorrowerEmailExists = await contactService.emailExists(coBorrower.email.trim());
        if (coBorrowerEmailExists) {
          toast.error(
            `A contact with co-borrower email "${coBorrower.email}" already exists in your organization. ` +
            `Please use a different email for the co-borrower.`
          );
          setLoading(false);
          return;
        }
      }

      // Check if co-borrower phone already exists
      if (addCoBorrower && coBorrower.mobilePhone.trim()) {
        const coBorrowerPhoneExists = await contactService.phoneExists(coBorrower.mobilePhone.trim());
        if (coBorrowerPhoneExists) {
          toast.error(
            `A contact with this co-borrower phone number already exists in your organization. ` +
            `Please use a different phone for the co-borrower.`
          );
          setLoading(false);
          return;
        }
      }

      // Generate household ID to link borrower and co-borrower
      const householdId = addCoBorrower ? crypto.randomUUID() : undefined;

      log.info('Creating borrower contact', {
        addCoBorrower,
        householdId,
        borrowerName: `${borrower.firstName} ${borrower.lastName}`
      });

      // Create primary borrower
      const primaryBorrower = await contactService.create({
        contactType: 'lead',
        firstName: borrower.firstName.trim(),
        lastName: borrower.lastName.trim(),
        salutation: borrower.salutation && borrower.salutation.trim() ? borrower.salutation.trim() : undefined,
        email: borrower.email.trim() || undefined,
        mobilePhone: borrower.mobilePhone.trim() || undefined,
        originalSource: 'manual_entry'
      });

      log.info('Primary borrower created', { borrowerId: primaryBorrower.id });

      // Create co-borrower if checkbox is checked
      if (addCoBorrower) {
        log.info('Creating co-borrower contact', {
          coBorrowerName: `${coBorrower.firstName} ${coBorrower.lastName}`,
          householdId
        });

        await contactService.create({
          contactType: 'lead',
          firstName: coBorrower.firstName.trim(),
          lastName: coBorrower.lastName.trim(),
          salutation: coBorrower.salutation && coBorrower.salutation.trim() ? coBorrower.salutation.trim() : undefined,
          email: coBorrower.email.trim() || undefined,
          mobilePhone: coBorrower.mobilePhone.trim() || undefined,
          originalSource: 'manual_entry',
          notes: `Co-borrower with ${borrower.firstName} ${borrower.lastName}`
        });

        log.info('Co-borrower created successfully');
        toast.success('Borrower and co-borrower added successfully');
      } else {
        toast.success('Borrower added successfully');
      }

      // Reset form
      setBorrower({ salutation: '', firstName: '', lastName: '', email: '', mobilePhone: '' });
      setCoBorrower({ salutation: '', firstName: '', lastName: '', email: '', mobilePhone: '' });
      setAddCoBorrower(false);
      setErrors({});

      // Call success callback to refresh list
      onSuccess();
      onClose();

    } catch (error) {
      log.error('Failed to create borrower', { error });

      // Standardized error handling for unique constraint violation
      let errorMessage = 'Failed to add borrower. Please try again.';

      if (error instanceof Error) {
        // Check for duplicate email constraint violation (23505 is PostgreSQL unique violation)
        if (error.message.includes('already exists') ||
            (error as any).code === '23505') {
          errorMessage = 'A contact with this email already exists. Please use a different email or update the existing contact.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setBorrower({ salutation: '', firstName: '', lastName: '', email: '', mobilePhone: '' });
      setCoBorrower({ salutation: '', firstName: '', lastName: '', email: '', mobilePhone: '' });
      setAddCoBorrower(false);
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Quick Add Borrower
          </DialogTitle>
          <DialogDescription>
            Add a new borrower contact to your organization.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Primary Borrower Section */}
          <div className="space-y-3">
            <h3 className="font-medium text-sm text-gray-700">Primary Borrower</h3>
            <p className="text-xs text-muted-foreground">* At least one contact method (email or phone) is required</p>

            <div className="space-y-2">
              <Label htmlFor="salutation">Title (Optional)</Label>
              <Select
                value={borrower.salutation || ''}
                onValueChange={(value) => setBorrower({ ...borrower, salutation: value })}
                disabled={loading}
              >
                <SelectTrigger id="salutation">
                  <SelectValue placeholder="Select title..." />
                </SelectTrigger>
                <SelectContent>
                  {HONORIFIC_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={borrower.firstName}
                  onChange={(e) => setBorrower({ ...borrower, firstName: e.target.value })}
                  placeholder="John"
                  disabled={loading}
                  className={errors.firstName ? 'border-red-500' : ''}
                />
                {errors.firstName && (
                  <p className="text-xs text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={borrower.lastName}
                  onChange={(e) => setBorrower({ ...borrower, lastName: e.target.value })}
                  placeholder="Doe"
                  disabled={loading}
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && (
                  <p className="text-xs text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={borrower.email}
                onChange={(e) => setBorrower({ ...borrower, email: e.target.value })}
                placeholder="john.doe@example.com"
                disabled={loading}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobilePhone">
                Cell Phone
              </Label>
              <Input
                id="mobilePhone"
                type="tel"
                value={borrower.mobilePhone}
                onChange={(e) => setBorrower({ ...borrower, mobilePhone: e.target.value })}
                placeholder="(555) 555-5555"
                disabled={loading}
                className={errors.mobilePhone ? 'border-red-500' : ''}
              />
              {errors.mobilePhone && (
                <p className="text-xs text-red-500">{errors.mobilePhone}</p>
              )}
            </div>
          </div>

          {/* Co-Borrower Checkbox */}
          <div className="flex items-center space-x-2 pt-2 border-t">
            <Checkbox
              id="addCoBorrower"
              checked={addCoBorrower}
              onCheckedChange={(checked) => setAddCoBorrower(checked === true)}
              disabled={loading}
            />
            <Label
              htmlFor="addCoBorrower"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
            >
              Add Co-Borrower
            </Label>
          </div>

          {/* Co-Borrower Section */}
          {addCoBorrower && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-md border border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-sm text-gray-700">Co-Borrower</h3>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setAddCoBorrower(false)}
                  disabled={loading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">* At least one contact method (email or phone) is required</p>

              <div className="space-y-2">
                <Label htmlFor="coBorrowerSalutation">Title (Optional)</Label>
                <Select
                  value={coBorrower.salutation || ''}
                  onValueChange={(value) => setCoBorrower({ ...coBorrower, salutation: value })}
                  disabled={loading}
                >
                  <SelectTrigger id="coBorrowerSalutation">
                    <SelectValue placeholder="Select title..." />
                  </SelectTrigger>
                  <SelectContent>
                    {HONORIFIC_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="coBorrowerFirstName">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="coBorrowerFirstName"
                    value={coBorrower.firstName}
                    onChange={(e) => setCoBorrower({ ...coBorrower, firstName: e.target.value })}
                    placeholder="Jane"
                    disabled={loading}
                    className={errors.coBorrowerFirstName ? 'border-red-500' : ''}
                  />
                  {errors.coBorrowerFirstName && (
                    <p className="text-xs text-red-500">{errors.coBorrowerFirstName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="coBorrowerLastName">
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="coBorrowerLastName"
                    value={coBorrower.lastName}
                    onChange={(e) => setCoBorrower({ ...coBorrower, lastName: e.target.value })}
                    placeholder="Doe"
                    disabled={loading}
                    className={errors.coBorrowerLastName ? 'border-red-500' : ''}
                  />
                  {errors.coBorrowerLastName && (
                    <p className="text-xs text-red-500">{errors.coBorrowerLastName}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="coBorrowerEmail">
                  Email
                </Label>
                <Input
                  id="coBorrowerEmail"
                  type="email"
                  value={coBorrower.email}
                  onChange={(e) => setCoBorrower({ ...coBorrower, email: e.target.value })}
                  placeholder="jane.doe@example.com"
                  disabled={loading}
                  className={errors.coBorrowerEmail ? 'border-red-500' : ''}
                />
                {errors.coBorrowerEmail && (
                  <p className="text-xs text-red-500">{errors.coBorrowerEmail}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="coBorrowerMobilePhone">
                  Cell Phone
                </Label>
                <Input
                  id="coBorrowerMobilePhone"
                  type="tel"
                  value={coBorrower.mobilePhone}
                  onChange={(e) => setCoBorrower({ ...coBorrower, mobilePhone: e.target.value })}
                  placeholder="(555) 555-5555"
                  disabled={loading}
                  className={errors.coBorrowerMobilePhone ? 'border-red-500' : ''}
                />
                {errors.coBorrowerMobilePhone && (
                  <p className="text-xs text-red-500">{errors.coBorrowerMobilePhone}</p>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Borrower{addCoBorrower ? 's' : ''}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
